import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart3, TrendingUp, TrendingDown, Minus } from 'lucide-react';

// Competitive Data for CONCACAF Teams
export const competitiveData = {
  usa: {
    name: 'United States',
    code: 'USA',
    flag: '🇺🇸',
    brandValueRange: { low: 240, high: 340 },
    overallScore: 71,
    yoyGrowth: 18.5,
    trend: 'up',
    position: 'Leader',
    historicalScores: [58, 62, 64, 71],
    color: '#3B82F6',
    isHighlighted: true,
  },
  mexico: {
    name: 'Mexico',
    code: 'MEX',
    flag: '🇲🇽',
    brandValueRange: { low: 180, high: 260 },
    overallScore: 68,
    yoyGrowth: 8.2,
    trend: 'stable',
    position: 'Challenger',
    historicalScores: [62, 64, 66, 68],
    color: '#22C55E',
    isHighlighted: false,
  },
  canada: {
    name: 'Canada',
    code: 'CAN',
    flag: '🇨🇦',
    brandValueRange: { low: 45, high: 75 },
    overallScore: 52,
    yoyGrowth: 34.2,
    trend: 'up',
    position: 'Rising',
    historicalScores: [32, 38, 45, 52],
    color: '#EF4444',
    isHighlighted: false,
  },
};

export const years = [2021, 2022, 2023, 2024];

// Position Badge Component
const PositionBadge = ({ position }) => {
  const config = {
    Leader: { bg: 'bg-primary/20', text: 'text-primary', border: 'border-primary/30' },
    Challenger: { bg: 'bg-warning/20', text: 'text-warning', border: 'border-warning/30' },
    Rising: { bg: 'bg-stable/20', text: 'text-stable', border: 'border-stable/30' },
  };
  const c = config[position] || config.Challenger;
  return (
    <Badge className={`${c.bg} ${c.text} ${c.border} border font-semibold`}>
      {position}
    </Badge>
  );
};

// Trend Icon Component
const TrendIcon = ({ trend }) => {
  if (trend === 'up') return <TrendingUp className="w-4 h-4 text-stable" />;
  if (trend === 'down') return <TrendingDown className="w-4 h-4 text-destructive" />;
  return <Minus className="w-4 h-4 text-warning" />;
};

// Format currency
const formatCurrency = (value) => `$${value}M`;

// Table Row Component
const BenchmarkRow = ({ team }) => (
  <tr 
    className={`border-b border-border/30 last:border-0 transition-colors ${
      team.isHighlighted 
        ? 'bg-primary/5 hover:bg-primary/10' 
        : 'hover:bg-muted/20'
    }`}
    data-testid={`benchmark-row-${team.code.toLowerCase()}`}
  >
    <td className="py-4 px-5">
      <div className="flex items-center gap-3">
        <span className="text-2xl">{team.flag}</span>
        <div>
          <span className={`font-semibold ${team.isHighlighted ? 'text-primary' : 'text-foreground'}`}>
            {team.name}
          </span>
          {team.isHighlighted && (
            <span className="ml-2 text-xs text-primary font-medium">(Host)</span>
          )}
        </div>
      </div>
    </td>
    <td className="py-4 px-5 text-right">
      <span className={`font-bold ${team.isHighlighted ? 'text-primary' : 'text-foreground'}`}>
        {formatCurrency(team.brandValueRange.low)} – {formatCurrency(team.brandValueRange.high)}
      </span>
    </td>
    <td className="py-4 px-5 text-center">
      <span className={`text-lg font-bold ${team.isHighlighted ? 'text-primary' : 'text-foreground'}`}>
        {team.overallScore}
      </span>
      <span className="text-muted-foreground text-sm">/100</span>
    </td>
    <td className="py-4 px-5 text-center">
      <span className={`font-semibold ${team.yoyGrowth > 15 ? 'text-stable' : team.yoyGrowth > 0 ? 'text-warning' : 'text-destructive'}`}>
        +{team.yoyGrowth}%
      </span>
    </td>
    <td className="py-4 px-5">
      <div className="flex items-center justify-center gap-2">
        <TrendIcon trend={team.trend} />
        <span className="text-sm text-muted-foreground capitalize">{team.trend}</span>
      </div>
    </td>
    <td className="py-4 px-5 text-center">
      <PositionBadge position={team.position} />
    </td>
  </tr>
);

// SECTION 1: Executive Benchmark Snapshot
export const BenchmarkSnapshot = () => {
  const teams = Object.values(competitiveData);
  
  return (
    <section className="mb-10" data-testid="benchmark-snapshot-section">
      <div className="flex items-center gap-3 mb-6">
        <BarChart3 className="w-6 h-6 text-primary" />
        <h2 className="text-lg font-semibold text-foreground tracking-tight">Executive Benchmark Snapshot</h2>
      </div>
      
      <Card className="bg-card border-border/50 overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="benchmark-table">
              <thead>
                <tr className="border-b border-border/50 bg-muted/30">
                  <th className="text-left py-4 px-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Country</th>
                  <th className="text-right py-4 px-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Brand Value Range</th>
                  <th className="text-center py-4 px-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Overall Score</th>
                  <th className="text-center py-4 px-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">YoY Growth</th>
                  <th className="text-center py-4 px-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">4-Year Trend</th>
                  <th className="text-center py-4 px-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Position</th>
                </tr>
              </thead>
              <tbody>
                {teams.map((team) => (
                  <BenchmarkRow key={team.code} team={team} />
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

export default BenchmarkSnapshot;
